import { Log } from '@aboutyou/legacy-shared'
import { SHOP_SIZE_RUNS } from 'constants/quickFilter'
import {
  adjustFilterValueBounds,
  intersectAvailableFilters,
  isFiltersFittingValueBounds,
} from 'helpers/filter'
import { redirect } from 'helpers/navigation'
import { FetchProps } from 'helpers/redial'
import { getSortChannel } from 'helpers/sort'
import { showProductStreamErrorToast } from 'helpers/toasts'
import { transaction } from 'mobx'
import { getAvailableFiltersWithAvailableAndSelectedValues } from 'pages/Category/helpers/getAvailableFiltersWithAvailableAndSelectedValues'
import {
  computeSelectedCategoryId,
  getIsEditorialPresent,
  updateInterleaveRowsAndProductTiles,
} from 'pages/Story/redial.helpers'
import { IService } from 'services'
import { IIdolRaw } from 'services/providers/iris/entities/idols/types'
import { BRAND_DETAIL_SET } from 'services/sets/brand'
import shallowEqual from 'shallowequal'
import handleQueryParamsForStreamPage from 'sharedComponents/ProductStream/redialHelpers/handleQueryParamsForStreamPage'
import ICategory from 'store/entities/Category/ICategory'
import Outfit from 'store/entities/Outfit/Outfit.store'
import Story from 'store/entities/Story/Story.store'
import { GenderType } from 'store/features/user/types'
import { SelectedLooseFilterMapType } from 'store/sidecar/FilterViewStore'
import PaginationViewStore from 'store/sidecar/PaginationViewStore'
import {
  loadFirstBatchAndStoryProducts,
  loadStory,
} from './redial-composers/storyLoading'

export default {
  fetch: async (props: FetchProps) => {
    const {
      params: { storyId },
      pathname,
      query,
      service,
      store,
    } = props

    const enableShopSizeRunFilters = store.feature.isActive(
      'enableShopSizeRunFilters',
    )

    let shouldShowErrorToast = false
    store.storyPage.startLoading()

    const loadStoryPromise = loadStory({ store, service, storyId })
    const navigationPromise = store.layoutStore.fetchNavigation()

    const [
      {
        associatedCategoryIds,
        introText,
        outfits,
        seo,
        story,
        idols,
        storyAttributeFilters,
        storyRows,
      },
    ] = await Promise.all([loadStoryPromise, navigationPromise])

    let selectedCategoryId: string | undefined
    if (query.category) {
      selectedCategoryId = query.category
    } else if (story.categoryId) {
      selectedCategoryId = String(story.categoryId)
    }

    let categoryRequest: Promise<ICategory> | undefined
    if (selectedCategoryId) {
      categoryRequest = service.categories.requestByCategoryId(
        selectedCategoryId,
        {
          strategy: 'cache_or_network',
        },
      )
    }

    const category = await categoryRequest?.catch(() => undefined)

    const {
      sortId,
      pageIsFiltered,
      queryNoneFilter,
      selectedFilters,
      currentPage,
    } = handleQueryParamsForStreamPage(
      props,
      new Set(category?.supportedFilter ?? []),
    )

    if (store.storyPage.storyId === storyId) {
      store.storyPage.endStoryLoading()
    }

    const isEditorialPresent = getIsEditorialPresent({
      pageIsFiltered,
      categoryId: queryNoneFilter.category,
      sortId,
    })

    redirectToStoryUrlOnPathMismatch({
      pathname,
      story,
      queryNoneFilter,
      selectedFilters,
    })
    redirectOnWrongCategoryQuery({
      query,
      associatedCategoryIds,
      queryNoneFilter,
      pathname,
      selectedFilters,
    })

    const sideEffectRequests = runAdditionalDataLoadingSideEffects({
      story,
      service,
    })

    const gender: GenderType =
      story.gender !== 'unisex' ? story.gender : store.user.gender

    await store.user.setGenderAndLoadNavigation(gender, {
      enforceOnClient: true,
    })

    let storyOutfits = outfits
    if (story.isIdolStory) {
      const { idolOutfits, idolStoryHeaderRow, failed } = await loadIdolOutfits(
        { idols, service, story },
      )
      shouldShowErrorToast = shouldShowErrorToast || failed

      storyRows.push(idolStoryHeaderRow)
      storyOutfits = idolOutfits
    }

    const { groupAttributeFilters, availableFilters } =
      await requestPossibleFilters({
        selectedCategoryId,
        selectedFilters,
        service,
        storyAttributeFilters,
        enableShopSizeRunFilters,
      })

    const sortChannel = getSortChannel({
      selectedFilters: { ...selectedFilters, ...groupAttributeFilters },
      sortId,
      enableUnsponsoredDefaultSorting: true,
    })

    const {
      pagination,
      currentBatch,
      duplicatesCount,
      productIds,
      activeAdditionalProducts,
    } = await loadFirstBatchAndStoryProducts({
      storyRows: Promise.resolve(storyRows),
      isEditorialPresent,
      sortChannel,
      sortId,
      categoryId: selectedCategoryId,
      sortingScore: store.storyPage.sorting.score,
      service,
      isIdolStory: story.isIdolStory,
      currentPage,
      selectedFilters,
      groupAttributeFilters,
      onError: e => {
        shouldShowErrorToast = true
        Log.warn(`Error fetching products for story ${story.id}`, {
          error: e,
        })
      },
      forceAddProducts: true,
    })

    redirectOnPaginationOverflow({
      currentBatch,
      lastPaginationBatch: pagination.last,
      shouldShowErrorToast,
      pathname,
      queryNoneFilter,
      selectedFilters,
    })

    if (pageIsFiltered) {
      const allAvailableFiltersByCategory = await service.filters
        .requestByCategoryId(selectedCategoryId, {
          selectedFilters: null,
          including: enableShopSizeRunFilters ? [SHOP_SIZE_RUNS] : undefined,
        })
        .catch(() => [])

      if (
        !isFiltersFittingValueBounds(
          selectedFilters,
          allAvailableFiltersByCategory,
        )
      ) {
        const adjustedFilters = adjustFilterValueBounds(
          selectedFilters,
          allAvailableFiltersByCategory,
        )

        throw redirect(
          pathname,
          { ...queryNoneFilter, ...adjustedFilters },
          {
            replaceState: true,
          },
        )
      }

      const selectedGroupFilterIds = groupAttributeFilters
        ? Object.keys(groupAttributeFilters).map(key => Number(key))
        : []

      const acceptedFilters = intersectAvailableFilters(
        selectedFilters,
        allAvailableFiltersByCategory.filter(availableFilter => {
          if (!availableFilter.id) {
            return true
          }

          return !selectedGroupFilterIds.includes(availableFilter.id)
        }),
      )

      if (!shallowEqual(acceptedFilters, selectedFilters)) {
        throw redirect(
          pathname,
          { ...queryNoneFilter, ...acceptedFilters },
          {
            replaceState: true,
          },
        )
      }
    }

    await sideEffectRequests

    transaction(() => {
      if (!selectedCategoryId) {
        const categoryId = store.layoutStore.getMappedCategoryId(
          'root',
          gender,
        ) as string

        selectedCategoryId = computeSelectedCategoryId({
          rootCategoryId: categoryId,
          service,
        })
      }

      if (!story.isIdolStory && productIds.length === 0) {
        store.storyPage.reset({
          introText,
          seo,
          storyRows,
          outfitIds: outfits.map(outfit => outfit.id),
          productIds: [],
          selectedCategoryId,
          storyId: story.id,
        })
        store.storyPage.filter.reset({
          categoryId: selectedCategoryId,
          selectedFilters,
          appliedSelectedLooseFilters: selectedFilters,
        })
        store.storyPage.pagination.reset({
          currentPage,
          total: 0,
          totalPages: 0,
          duplicatesCount: 0,
          additionalCount: 0,
        })
        store.storyPage.sorting.reset({
          appliedProductsSortingId: sortId,
          sortChannel,
        })
        store.storyPage.category.reset({
          id: selectedCategoryId,
          isSale: false,
          availableCategoryIds: associatedCategoryIds,
          sustainable: category ? category.sustainable : false,
          showProductName: category ? category.showProductName : false,
          categoryContext: category ? category.categoryContext : undefined,
          disableSizeFilter: category ? category.disableSizeFilter : false,
        })
        store.storyPage.rowBuilder.resetInterleaveRows([])

        store.storyPage.endStreamLoading()
        store.storyPage.endStoryLoading()

        if (shouldShowErrorToast) {
          showProductStreamErrorToast(store.toastNotifications, store.t)
        }

        return
      }

      store.storyPage.reset({
        introText,
        seo,
        storyRows,
        outfitIds: storyOutfits.map(outfit => outfit.id),
        productIds,
        selectedCategoryId,
        storyId: story.id,
      })
      store.storyPage.filter.reset({
        availableFilters: story.isIdolStory ? [] : availableFilters,
        categoryId: selectedCategoryId,
        selectedGroupFilters: groupAttributeFilters,
        selectedFilters: story.isIdolStory ? {} : selectedFilters,
        appliedSelectedLooseFilters: story.isIdolStory ? {} : selectedFilters,
      })
      store.storyPage.pagination.reset({
        currentPage,
        total: pagination.total,
        totalPages: PaginationViewStore.totalBatchesToTotalPages(
          pagination.last,
        ),
        duplicatesCount,
        additionalCount: activeAdditionalProducts.length,
      })
      store.storyPage.sorting.reset({
        appliedProductsSortingId: sortId,
        sortChannel,
      })
      store.storyPage.category.reset({
        id: selectedCategoryId,
        isSale: false,
        availableCategoryIds: associatedCategoryIds,
        sustainable: category ? category.sustainable : false,
        showProductName: category ? category.showProductName : false,
        categoryContext: category ? category.categoryContext : undefined,
        disableSizeFilter: category ? category.disableSizeFilter : false,
      })

      /**
       * tilesPerRow can change on client side redial execution
       * because of different media queries and default value of 4
       * on server side.
       * We don't want to interrupt the user experience and therefore
       * changes are triggered only on page navigation.
       */
      updateInterleaveRowsAndProductTiles(store.storyPage, isEditorialPresent)

      store.storyPage.endStreamLoading()
      store.storyPage.endStoryLoading()

      if (shouldShowErrorToast) {
        showProductStreamErrorToast(store.toastNotifications, store.t)
      }
    })
  },
  // TODO: This is a workaround to fix the brand logo not loading on direct entry https://aboutyou.atlassian.net/browse/ASFE-11605
  defer: async (props: FetchProps) => {
    const {
      params: { storyId },
      service,
      store,
    } = props

    const { story } = await loadStory({ store, service, storyId })

    await runAdditionalDataLoadingSideEffects({
      story,
      service,
    })
  },
}

function redirectToStoryUrlOnPathMismatch({
  pathname,
  story,
  selectedFilters,
  queryNoneFilter,
}: {
  pathname: string
  story: Story
  selectedFilters: SelectedLooseFilterMapType
  queryNoneFilter: {
    [name: string]: string
  }
}) {
  if (pathname !== story.url) {
    const { category, ...queryNoneFilterNoneCategory } = queryNoneFilter

    throw redirect(
      story.url,
      { ...queryNoneFilterNoneCategory, ...selectedFilters },
      {
        replaceState: true,
      },
    )
  }
}

async function runAdditionalDataLoadingSideEffects({
  service,
  story,
}: {
  story: Story
  service: IService
}) {
  const requests: Promise<unknown>[] = []
  if (story.headerImageBrandId) {
    requests.push(
      service.brands.requestById(story.headerImageBrandId, BRAND_DETAIL_SET),
    )
  }

  try {
    await Promise.all(requests)
  } catch (error) {
    Log.error(`Failed to load additional data for story ${story.id}`, error)
  }
}

function redirectOnPaginationOverflow({
  currentBatch,
  lastPaginationBatch,
  pathname,
  queryNoneFilter,
  selectedFilters,
  shouldShowErrorToast,
}: {
  currentBatch: number
  lastPaginationBatch: number
  pathname: string
  queryNoneFilter: Record<string, string>
  selectedFilters: SelectedLooseFilterMapType
  shouldShowErrorToast: boolean
}) {
  if (currentBatch > lastPaginationBatch && !shouldShowErrorToast) {
    throw redirect(
      pathname,
      { ...queryNoneFilter, ...selectedFilters, page: undefined },
      {
        replaceState: true,
      },
    )
  }
}

function redirectOnWrongCategoryQuery({
  query,
  associatedCategoryIds,
  queryNoneFilter,
  pathname,
  selectedFilters,
}: {
  query: {
    [key: string]: string
  }
  associatedCategoryIds: string[]
  queryNoneFilter: {
    [name: string]: string
  }
  pathname: string
  selectedFilters: SelectedLooseFilterMapType
}) {
  if (query.category && !associatedCategoryIds.includes(query.category)) {
    const { category, ...queryNoneFilterNoneCategory } = queryNoneFilter

    throw redirect(
      pathname,
      { ...queryNoneFilterNoneCategory, ...selectedFilters },
      {
        replaceState: true,
      },
    )
  }
}

async function loadIdolOutfits({
  service,
  story,
  idols,
}: {
  service: IService
  story: Story
  idols: IIdolRaw[]
}) {
  let failed = false
  const { outfits: idolOutfits } = await service.outfits
    .requestByIdolId(story.idolId)
    .catch(e => {
      failed = true
      Log.warn(
        `Error while fetching outfits for idol ${story.idolId} on story ${story.id}`,
        { error: e },
      )
      return {
        outfits: [] as Outfit[],
      }
    })

  const idol = idols.find(idol => idol.id === story.idolId)

  // Subline might come close to the idol name as a fallback,
  // but in reality, we should always find our idol
  const idolName = idol ? idol.name : story.subline

  const idolStoryHeaderRow = {
    type: 'idol-story-outfit-header',
    id: 'idol-story-outfit-header',
    idolName,
    outfitCount: idolOutfits.length,
  } as const

  return {
    idolOutfits,
    idolStoryHeaderRow,
    failed,
  }
}

async function requestPossibleFilters({
  selectedCategoryId,
  storyAttributeFilters,
  selectedFilters,
  service,
  enableShopSizeRunFilters = false,
}: {
  selectedCategoryId: string
  storyAttributeFilters: Record<number, Record<number, number[]> | undefined>
  selectedFilters: SelectedLooseFilterMapType
  service: IService
  enableShopSizeRunFilters: boolean
}) {
  const groupAttributeFilters =
    storyAttributeFilters[Number(selectedCategoryId)] || {}
  const { minPrice, maxPrice, ...selectedWithoutPricesFilters } =
    selectedFilters

  const availableFilters = await service.filters.requestByCategoryId(
    selectedCategoryId,
    {
      selectedFilters: {
        ...selectedWithoutPricesFilters,
        ...groupAttributeFilters,
      },
      including: enableShopSizeRunFilters ? [SHOP_SIZE_RUNS] : undefined,
    },
    { strategy: 'cache_or_network' },
  )

  const finalAvailableFilters =
    await getAvailableFiltersWithAvailableAndSelectedValues(
      availableFilters,
      selectedFilters,
      {
        loadAttributesByFilterNames:
          service.filters.requestAttributesByFilterNames,
      },
    )

  return { groupAttributeFilters, availableFilters: finalAvailableFilters }
}
